import type { MasterSetupPhase } from './masterSetupFlow';
import type { ExtendedServiceHubType } from './serviceHubs';
import type { MasterCatalogGroup } from './masters';

/** Food Add — 18 masters; inventory/marketing in separate modules */
export const FOOD_DELIVERY_SETUP: MasterSetupPhase[] = [
  {
    id: 'restaurant',
    title: '1. Restaurant & Branch',
    subtitle: 'Multi-outlet SaaS',
    color: '#db2777',
    steps: [
      { groupKey: '_external', typeKey: 'restaurants', label: 'Restaurant Master', why: 'Full restaurant profile — owner, zone, GST/FSSAI, service modes, timing, approval, commission or subscription.', example: 'Spice Hub · slug: spice_hub' },
      { groupKey: 'food_masters', typeKey: 'restaurant_types', label: 'Restaurant Type', why: 'Restaurant, cloud kitchen, cafe — with cover images.', example: 'restaurant · cloud_kitchen · food_court' },
      { groupKey: 'food_masters', typeKey: 'restaurant_opening_hours', label: 'Opening Hours', why: 'Standard open/close templates by day — applied on Add Restaurant.', example: 'Mon 10:00–22:00 · Sat 09:00–23:00' },
      { groupKey: 'food_masters', typeKey: 'restaurant_branches', label: 'Branch Master', why: 'Main and sub branches per restaurant.', example: 'spice_hub_main · Koramangala' },
    ],
  },
  {
    id: 'classify',
    title: '2. Classification',
    subtitle: 'Category, sub-category, cuisine, dietary, tags',
    color: '#ea580c',
    steps: [
      { groupKey: 'food_masters', typeKey: 'food_categories', label: 'Category', why: 'Pizza, Burger, Biryani, Beverages.', example: 'pizza · burger · biryani' },
      { groupKey: 'food_masters', typeKey: 'food_sub_categories', label: 'Sub Category', why: 'Veg Pizza, Non Veg Pizza, Soft Drinks.', example: 'veg_pizza · Parent: pizza', tips: ['Set Parent Category Code'] },
      { groupKey: 'food_masters', typeKey: 'cuisine_types', label: 'Cuisine', why: 'South Indian, Chinese, Italian.', example: 'south_indian · chinese' },
      { groupKey: 'food_masters', typeKey: 'dietary_types', label: 'Dietary Type', why: 'Veg, Non Veg, Vegan, Halal.', example: 'veg · non_veg · jain' },
      { groupKey: 'food_masters', typeKey: 'food_tags', label: 'Food Tags', why: 'Bestseller, Recommended, New.', example: 'bestseller · trending' },
    ],
  },
  {
    id: 'attributes',
    title: '3. Attributes & Variants',
    subtitle: 'Configurable options → generated variants',
    color: '#2563eb',
    steps: [
      { groupKey: 'food_masters', typeKey: 'food_attributes', label: 'Food Attributes', why: 'Size, Crust, Spice Level, Sugar Level.', example: 'size · crust · spice_level' },
      { groupKey: 'food_masters', typeKey: 'food_attribute_values', label: 'Attribute Values', why: 'Small/Medium/Large under Size.', example: 'small · Attribute: size', tips: ['Set Attribute Code field'] },
      { groupKey: 'food_masters', typeKey: 'food_variants', label: 'Variant Templates', why: 'Reference variants with SKU and cost price.', example: 'small_pizza · SKU · Price · Cost' },
    ],
  },
  {
    id: 'addons',
    title: '4. Addons',
    subtitle: 'Groups and modifiers',
    color: '#7c3aed',
    steps: [
      { groupKey: 'food_masters', typeKey: 'addon_groups', label: 'Addon Group', why: 'Min/max/required rules.', example: 'pizza_toppings · Min: 0 · Max: 5' },
      { groupKey: 'food_masters', typeKey: 'food_addons', label: 'Addon', why: 'Cheese, Chicken, Coke.', example: 'extra_cheese · Group: pizza_toppings · ₹40' },
    ],
  },
  {
    id: 'pricing',
    title: '5. Tax, Unit, Kitchen',
    subtitle: 'Pricing support masters',
    color: '#0369a1',
    steps: [
      { groupKey: 'food_masters', typeKey: 'food_tax_groups', label: 'Tax Group', why: 'GST 5%, 12%, 18%.', example: 'gst_5 · Tax: 5%' },
      { groupKey: 'food_masters', typeKey: 'food_units', label: 'Unit', why: 'Piece, Plate, Bowl, Kg.', example: 'piece · plate · bowl' },
      { groupKey: 'food_masters', typeKey: 'kitchen_stations', label: 'Kitchen Station', why: 'Main Kitchen, Bakery, Grill.', example: 'main_kitchen · bakery' },
      { groupKey: 'food_masters', typeKey: 'preparation_times', label: 'Preparation Time', why: '10, 15, 20 minutes.', example: 'prep_15 · Minutes: 15' },
    ],
  },
  {
    id: 'availability',
    title: '6. Availability & Images',
    subtitle: 'Schedule templates and image masters',
    color: '#059669',
    steps: [
      { groupKey: 'food_masters', typeKey: 'food_availability_schedules', label: 'Availability Schedule', why: 'Day/start/end templates.', example: 'Mon · 09:00–22:00' },
      { groupKey: 'food_masters', typeKey: 'food_images', label: 'Food Images', why: 'Thumbnail/gallery templates per food code.', example: 'biryani_full · gallery' },
    ],
  },
  {
    id: 'food_add',
    title: '7. Food Add',
    subtitle: '6-tab wizard — catalog only',
    color: '#dc2626',
    steps: [{
      groupKey: '_external', typeKey: 'food_add_wizard', label: 'Food Add Screen',
      why: 'Basic → Pricing & Variants → Addons → Availability → Media → Settings. No inventory/recipe/discount.',
      externalLink: '/food/items/new',
      tips: ['Complete all 17 masters above first'],
    }],
  },
  {
    id: 'separate',
    title: 'Separate Modules (not in Food Add)',
    subtitle: 'Configure in Inventory, Purchase, Marketing modules',
    color: '#64748b',
    steps: [
      { groupKey: '_external', typeKey: 'inventory_module', label: 'Ingredient & Recipe', why: 'Ingredient, Recipe, Stock — Inventory module.', externalLink: '/food/menu' },
      { groupKey: '_external', typeKey: 'marketing_module', label: 'Discount & Offers', why: 'Discount, Coupon, Combo — Marketing module.', externalLink: '/config/promos' },
    ],
  },
  {
    id: 'admin_ops',
    title: 'Food Delivery Operations',
    subtitle: 'Admin functions — Swiggy-like platform controls',
    color: '#7c2d12',
    steps: [
      { groupKey: '_external', typeKey: 'restaurant_onboarding', label: 'Restaurant Onboarding + FSSAI', why: 'Partner KYC and document verification.', externalLink: '/onboarding' },
      { groupKey: '_external', typeKey: 'zones_delivery', label: 'Zones + Partner Pricing + Incentives', why: 'Polygon coverage, food customer fee, partner earnings, incentive options.', externalLink: '/zones?hub=delivery&service=food-delivery' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_partner_pricing', label: 'Partner Pricing (zone)', why: 'Optional master fallback — prefer Zone Partner Pricing. Set Service = Food Delivery.', example: 'BLR_CENTRAL · food-delivery · base ₹35' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_incentives', label: 'Incentive Options', why: 'Peak, rain, night, heavy, multi, festival — set Service = Food Delivery.', example: 'peak_hour · ₹20 · food-delivery' },
      { groupKey: '_external', typeKey: 'dispatcher_food', label: 'Auto-Dispatch Partners', why: 'Live dispatcher for manual override.', externalLink: '/dispatcher' },
      { groupKey: '_external', typeKey: 'promos_food', label: 'Promo Codes + Discounts', why: 'Platform and restaurant-level offers.', externalLink: '/config/promos' },
      { groupKey: 'delivery_masters', typeKey: 'cancellation_reasons', label: 'Cancellation + Refund Policy', why: 'Cancel reasons and refund rules.', example: 'item_unavailable' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_slots', label: 'Delivery Slots', why: 'Peak-hour and scheduled delivery windows.', example: 'lunch · dinner' },
      { groupKey: '_external', typeKey: 'reports_food', label: 'Analytics + Order Reports', why: 'Restaurant and platform order insights.', externalLink: '/reports' },
      { groupKey: '_external', typeKey: 'settlements_food', label: 'Commission + Settlements', why: 'Merchant payout ledger.', externalLink: '/settlements' },
    ],
  },
];

export const GROCERY_DELIVERY_SETUP: MasterSetupPhase[] = [
  {
    id: 'store_types',
    title: '1. Store & Types',
    subtitle: 'Store master, types, categories',
    color: '#16a34a',
    steps: [
      { groupKey: '_external', typeKey: 'grocery_stores', label: 'Store Master', why: 'Create grocery stores with type, hours, and logo.', externalLink: '/grocery/stores', example: 'Demo Mart · dark_store' },
      { groupKey: 'grocery_masters', typeKey: 'grocery_store_types', label: 'Store Types', why: 'Hypermarket, dark store, organic — with logo images.', example: 'hypermarket · dark_store' },
      { groupKey: 'grocery_masters', typeKey: 'grocery_categories', label: 'Categories', why: 'Fruits, Dairy, Snacks — upload category images.', example: 'fruits · dairy · snacks' },
      { groupKey: 'grocery_masters', typeKey: 'grocery_sub_categories', label: 'Sub Categories', why: 'Apple, Milk under parent category.', example: 'apple · Parent: fruits', tips: ['Set Parent Category Code'] },
      { groupKey: 'grocery_masters', typeKey: 'grocery_brands', label: 'Brands', why: 'Amul, Nestle — brand logos for mobile browse.', example: 'amul · nestle · britannia' },
    ],
  },
  {
    id: 'catalog',
    title: '2. Warehouses & Variants',
    subtitle: 'Fulfillment, SKU templates, tax',
    color: '#059669',
    steps: [
      { groupKey: 'grocery_masters', typeKey: 'warehouses', label: 'Warehouses', why: 'Fulfillment centers with location images.', example: 'wh_blr_north · Bangalore North DC' },
      { groupKey: 'grocery_masters', typeKey: 'product_variants', label: 'Product Variants', why: '500g / 1kg / family pack.', example: '500g · 1kg · 2kg' },
      { groupKey: 'grocery_masters', typeKey: 'weight_types', label: 'Weight Types', why: 'Unit of measure for products.', example: 'kg · gram · liter · piece' },
      { groupKey: 'grocery_masters', typeKey: 'grocery_tax_groups', label: 'Tax Groups', why: 'GST 0%, 5%, 12% for grocery.', example: 'gst_5 · Tax: 5%' },
      { groupKey: 'grocery_masters', typeKey: 'suppliers', label: 'Suppliers', why: 'Vendor sourcing for inventory.', example: 'fresh_farms · metro_wholesale' },
      { groupKey: 'grocery_masters', typeKey: 'stock_alerts', label: 'Stock Alerts', why: 'Low-stock notification thresholds.', example: 'critical · low · adequate' },
    ],
  },
  {
    id: 'images',
    title: '3. Images & Availability',
    subtitle: 'Product images and delivery windows',
    color: '#0369a1',
    steps: [
      { groupKey: 'grocery_masters', typeKey: 'grocery_product_images', label: 'Product Images', why: 'Thumbnail/gallery per SKU code.', example: 'APPLE_1KG · thumbnail' },
      { groupKey: 'grocery_masters', typeKey: 'grocery_availability_schedules', label: 'Availability Schedules', why: 'Store delivery windows by day.', example: 'Mon · 07:00–22:00' },
    ],
  },
  {
    id: 'grocery_add',
    title: '4. Grocery Add',
    subtitle: '6-tab wizard — full catalog flow',
    color: '#dc2626',
    steps: [{
      groupKey: '_external', typeKey: 'grocery_add_wizard', label: 'Grocery Add Screen',
      why: 'Basic → Pricing & Stock → Supply → Availability → Media → Settings. All 12 masters wired.',
      externalLink: '/grocery/products/new',
      tips: ['Complete all 12 grocery masters above first'],
    }],
  },
  {
    id: 'delivery_ops',
    title: '5. Delivery Operations',
    subtitle: 'Slots, partner pricing, incentives, substitution',
    color: '#0891b2',
    steps: [
      { groupKey: 'delivery_masters', typeKey: 'delivery_slots', label: 'Delivery Slots', why: 'Grocery delivery time windows.', example: 'morning 7-10 · evening 5-8' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_partner_pricing', label: 'Partner Pricing (zone)', why: 'Optional master fallback for partner earnings — prefer Zone Partner Pricing step. Set Service = Grocery Delivery.', example: 'TIRUPUR · grocery-delivery · base ₹35' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_incentives', label: 'Incentive Options', why: 'Peak, rain, night, heavy, multi, festival bonuses — set Service = Grocery Delivery.', example: 'peak_hour · ₹20 · grocery-delivery' },
      { groupKey: 'delivery_masters', typeKey: 'delivery_partners_tiers', label: 'Partner Tiers', why: 'Bronze → Silver → Gold by Min Orders; Display Order sets precedence; Bonus is % of earnings.', example: 'bronze · 0 orders · 0% · order 1' },
      { groupKey: 'delivery_masters', typeKey: 'substitution_rules', label: 'Substitution Rules', why: 'Out-of-stock substitute policy.', example: 'allow_similar_brand · call_customer' },
      { groupKey: 'delivery_masters', typeKey: 'cancellation_reasons', label: 'Cancellation Reasons', why: 'Order cancel codes.', example: 'item_unavailable · slot_missed' },
    ],
  },
  {
    id: 'admin_ops',
    title: '7. Admin Operations',
    subtitle: 'Admin functions — store, slot, and dispatch controls',
    color: '#14532d',
    steps: [
      { groupKey: '_external', typeKey: 'store_onboarding', label: 'Store Onboarding + Verification', why: 'Grocery partner KYC.', externalLink: '/onboarding' },
      { groupKey: '_external', typeKey: 'grocery_products', label: 'Product Catalog + Inventory', why: 'Products, variants, stock levels.', externalLink: '/grocery/products' },
      { groupKey: '_external', typeKey: 'zones_grocery', label: 'Zones + Partner Pricing', why: 'Coverage polygons, grocery customer fee, partner earnings, incentives.', externalLink: '/zones?hub=delivery&service=grocery-delivery' },
      { groupKey: '_external', typeKey: 'dispatcher_grocery', label: 'Auto-Dispatch Partners', why: 'Manual assign override.', externalLink: '/dispatcher' },
      { groupKey: '_external', typeKey: 'promos_grocery', label: 'Promo Codes + Offers', why: 'Platform grocery promotions.', externalLink: '/config/promos' },
      { groupKey: '_external', typeKey: 'reports_grocery', label: 'Store Analytics + Reports', why: 'Order and revenue insights.', externalLink: '/reports' },
      { groupKey: '_external', typeKey: 'settlements_grocery', label: 'Commission + Settlements', why: 'Store payout ledger.', externalLink: '/settlements' },
    ],
  },
];

export const TRANSPORT_SETUP: MasterSetupPhase[] = [
  {
    id: 'coverage',
    title: 'Zones',
    subtitle: 'Zone Management (external)',
    color: '#0891b2',
    steps: [{
      groupKey: '_external', typeKey: 'zones', label: 'Zones & Coverage',
      why: 'Operational zones and fare rules — not in Service Add.',
      externalLink: '/zones',
    }],
  },
  {
    id: 'fleet',
    title: '1. Fleet & Branch',
    subtitle: 'Multi-city operator branches',
    color: '#2563eb',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'fleet_branches', label: 'Fleet Branch', why: 'Operator depots per city.', example: 'navngo_blr_main' },
      { groupKey: 'transport_masters', typeKey: 'trip_types', label: 'Trip Type', why: 'LOCAL, RENTAL, OUTSTATION.', example: 'LOCAL · Service Code: LOCAL' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_types', label: 'Vehicle Type', why: 'Bike, auto, sedan — links zone fare rows.', example: 'bike · Capacity: 1' },
    ],
  },
  {
    id: 'vehicle',
    title: '2. Vehicle Taxonomy',
    subtitle: 'Brand, model, variant, color, fuel',
    color: '#7c3aed',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'vehicle_brands', label: 'Vehicle Brand', why: 'Honda, Bajaj, Tata.', example: 'honda · bajaj' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_models', label: 'Vehicle Model', why: 'Models under brand.', example: 'activa_6g · Parent: honda' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_variants', label: 'Vehicle Variant', why: 'Trim under model.', example: 'dzire_vxi' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_colors', label: 'Vehicle Color', why: 'Fleet color picker.', example: 'white · #ffffff' },
      { groupKey: 'transport_masters', typeKey: 'fuel_types', label: 'Fuel Type', why: 'Petrol, EV, CNG.', example: 'petrol · electric' },
      { groupKey: 'transport_masters', typeKey: 'transmission_types', label: 'Transmission', why: 'Manual, automatic.', example: 'manual · automatic' },
      { groupKey: 'transport_masters', typeKey: 'permit_types', label: 'Permit Type', why: 'Commercial permits.', example: 'all_india · state' },
    ],
  },
  {
    id: 'attributes',
    title: '3. Ride Attributes',
    subtitle: 'Configurable service options',
    color: '#0369a1',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'ride_attributes', label: 'Ride Attribute', why: 'AC, Luggage, Pet-friendly.', example: 'ac · luggage · women_only' },
      { groupKey: 'transport_masters', typeKey: 'ride_attribute_values', label: 'Attribute Values', why: 'Values per attribute.', example: 'ac_yes · Attribute: ac' },
      { groupKey: 'transport_masters', typeKey: 'ride_tags', label: 'Ride Tags', why: 'Featured, Premium, Popular.', example: 'featured · premium' },
    ],
  },
  {
    id: 'availability',
    title: '4. Availability & Media',
    subtitle: 'Schedules and service images',
    color: '#059669',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'service_availability_schedules', label: 'Availability Schedule', why: 'Day/time service windows.', example: 'Mon · 06:00–23:00' },
      { groupKey: 'transport_masters', typeKey: 'ride_images', label: 'Ride Images', why: 'Service icons and gallery.', example: 'economy_ride · icon' },
    ],
  },
  {
    id: 'ops',
    title: '7. Admin Operations',
    subtitle: 'Admin functions — not in Service Add wizard',
    color: '#64748b',
    steps: [
      { groupKey: '_external', typeKey: 'zones_admin', label: 'Zones + Fare Matrix', why: 'Polygon map, per-service pricing, surge rules.', externalLink: '/zones' },
      { groupKey: '_external', typeKey: 'dispatcher', label: 'Dispatcher Live Map', why: 'Manual assign override for open rides.', externalLink: '/dispatcher' },
      { groupKey: '_external', typeKey: 'driver_onboarding', label: 'Driver Onboarding + KYC', why: 'Partner registration — assign payout model at onboarding.', externalLink: '/onboarding' },
      { groupKey: 'transport_ops_masters', typeKey: 'driver_document_types', label: 'Driver Documents', why: 'KYC document types.', example: 'driving_license' },
      { groupKey: 'transport_ops_masters', typeKey: 'vehicle_documents', label: 'Vehicle Documents', why: 'RC, insurance — fleet compliance.', example: 'rc_book · insurance' },
      { groupKey: 'transport_ops_masters', typeKey: 'transport_cancellation_reasons', label: 'Cancel Reasons + Penalties', why: 'Cancellation taxonomy with fee rules.', example: 'driver_no_show' },
      { groupKey: 'transport_ops_masters', typeKey: 'driver_incentive_types', label: 'Incentives & Bonuses', why: 'Peak-hour and target bonus programs.', example: 'peak_hour_bonus' },
      { groupKey: 'transport_ops_masters', typeKey: 'sos_contacts', label: 'SOS Emergency Alerts', why: 'Emergency contacts and alert types.', example: 'panic_button' },
      { groupKey: '_external', typeKey: 'settlements', label: 'Settlements (Commission)', why: 'Commission-model driver payout ledger.', externalLink: '/settlements' },
    ],
  },
];

export const ON_DEMAND_SETUP: MasterSetupPhase[] = [
  {
    id: 'services',
    title: '1. Service Catalog',
    subtitle: 'Categories (primary → sub), skills, certificates, experience',
    color: '#059669',
    steps: [
      { groupKey: 'on_demand_masters', typeKey: 'service_categories', label: 'Service Categories', why: 'Primary → sub hierarchy for the Services catalog.', example: 'electrical → ac_repair · plumbing → leak_fix' },
      { groupKey: 'on_demand_masters', typeKey: 'skills', label: 'Skills', why: 'Provider skill tags for matching & packages.', example: 'ac_repair · wiring · pipe_fix' },
      { groupKey: 'on_demand_masters', typeKey: 'certificates', label: 'Certificates', why: 'Provider KYC / trade docs for onboarding.', example: 'electrician_license · police_verification' },
      { groupKey: 'on_demand_masters', typeKey: 'experience_levels', label: 'Experience Levels', why: 'Junior / senior pricing tiers.', example: 'junior · senior · expert' },
    ],
  },
  {
    id: 'pricing',
    title: '2. Pricing',
    subtitle: 'Package pricing blueprints vs doorstep visit fees',
    color: '#0369a1',
    steps: [
      {
        groupKey: 'on_demand_masters',
        typeKey: 'pricing_templates',
        label: 'Pricing Templates',
        why: 'Package pricing model blueprint (fixed / hourly / per visit) — base labour price structure used when defining packages.',
        example: 'fixed_visit · hourly · emergency',
      },
      {
        groupKey: 'on_demand_masters',
        typeKey: 'visit_charges',
        label: 'Visit Charges',
        why: 'Call-out / doorstep fee for the provider visiting the customer (often added on top of base package price).',
        example: 'standard_visit · emergency_visit',
      },
    ],
  },
  {
    id: 'ops',
    title: '3. Operations',
    subtitle: 'Cancellation reasons',
    color: '#7c3aed',
    steps: [
      { groupKey: 'on_demand_masters', typeKey: 'cancellation_reasons', label: 'Cancellation Reasons', why: 'Customer/provider cancel taxonomy.', example: 'customer_changed_mind · provider_unavailable' },
    ],
  },
];

export const VIDEO_CONSULTATION_SETUP: MasterSetupPhase[] = [
  {
    id: 'consult',
    title: '1. Consultation Setup',
    subtitle: 'Types, specializations, and session rules',
    color: '#db2777',
    steps: [
      { groupKey: 'video_consultation_masters', typeKey: 'consultation_types', label: 'Consultation Types', why: 'Doctor, tutor, astrology, etc.', example: 'medical · tutoring · astrology' },
      { groupKey: 'video_consultation_masters', typeKey: 'expert_specializations', label: 'Specializations', why: 'Expert sub-domains.', example: 'cardiology · dermatology · vedic_astrology' },
      { groupKey: 'video_consultation_masters', typeKey: 'session_durations', label: 'Session Durations', why: '15 / 30 / 60 min slots.', example: '15min · 30min · 60min' },
      { groupKey: 'video_consultation_masters', typeKey: 'prescription_templates', label: 'Prescription Templates', why: 'Medical note templates.', example: 'general_consult · follow_up' },
      { groupKey: 'video_consultation_masters', typeKey: 'consultation_languages', label: 'Languages', why: 'Consultant language options.', example: 'english · hindi · tamil' },
    ],
  },
];

export const BOOKING_SETUP: MasterSetupPhase[] = [
  {
    id: 'events',
    title: '1. Event Booking',
    subtitle: 'Organizers, venues, and policies',
    color: '#7c2d12',
    steps: [
      { groupKey: 'events_masters', typeKey: 'organizer_types', label: 'Organizer Types', why: 'Event host classification.', example: 'cinema · venue · salon · hotel' },
      { groupKey: 'events_masters', typeKey: 'venues', label: 'Venues', why: 'Physical venue master.', example: 'pvr_forum · convention_hall_a' },
      { groupKey: 'events_masters', typeKey: 'cancellation_policies', label: 'Cancellation Policies', why: 'Refund rules by time window.', example: 'flexible_24h · strict_no_refund' },
      { groupKey: 'events_masters', typeKey: 'refund_policies', label: 'Refund Policies', why: 'Partial/full refund rules.', example: 'full_before_24h · partial_50' },
    ],
  },
];

export const RENTAL_SETUP: MasterSetupPhase[] = [
  {
    id: 'coverage',
    title: 'Zones',
    subtitle: 'Zone Management (external)',
    color: '#0891b2',
    steps: [{
      groupKey: '_external', typeKey: 'zones', label: 'Zones & Coverage',
      why: 'Rental packages are zone-scoped — same as transport rental master.',
      externalLink: '/zones',
    }],
  },
  {
    id: 'depots',
    title: '1. Fleet & Depots',
    subtitle: 'Pickup branches for bike/car rental',
    color: '#2563eb',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'fleet_branches', label: 'Fleet Branch / Depot', why: 'Rental pickup hubs per city.', example: 'rental_blr_main · car-rental' },
      { groupKey: 'transport_masters', typeKey: 'trip_types', label: 'Trip Type — RENTAL', why: 'Required for rental bookings.', example: 'RENTAL · Service Code: RENTAL' },
    ],
  },
  {
    id: 'vehicles',
    title: '2. Rental Vehicles',
    subtitle: 'Vehicle types with RENTAL service type',
    color: '#7c3aed',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'vehicle_types', label: 'Vehicle Type', why: 'Set serviceTypes to include RENTAL (like transport Vehicle master).', example: 'bike · serviceTypes: LOCAL,RENTAL' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_brands', label: 'Vehicle Brand', why: 'Optional fleet taxonomy.', example: 'honda · bajaj' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_models', label: 'Vehicle Model', why: 'Models under brand.', example: 'activa_6g' },
    ],
  },
  {
    id: 'packages',
    title: '3. Rental Packages',
    subtitle: 'Hour/km bundles with per-vehicle pricing',
    color: '#059669',
    steps: [
      { groupKey: '_external', typeKey: 'rental_packages_tab', label: 'Rental Package Management', why: 'Create hour/km packages with vehicle price grid — like transport rental master.', example: '4hr/40km · Sedan ₹899 · SUV ₹1199' },
      { groupKey: 'transport_masters', typeKey: 'rental_packages', label: 'Rental Package Master', why: 'Package records per vehicle type.', example: 'BLR_4H40_SEDAN · grace 15min · extra km ₹12' },
    ],
  },
];

export const OUTSTATION_SETUP: MasterSetupPhase[] = [
  {
    id: 'coverage',
    title: 'Zones',
    subtitle: 'Zone Management (external)',
    color: '#0891b2',
    steps: [{
      groupKey: '_external', typeKey: 'zones', label: 'Zones & Coverage',
      why: 'Outstation pricing is zone-scoped — pickup zone determines which fare rows apply.',
      externalLink: '/zones',
    }],
  },
  {
    id: 'vehicles',
    title: '1. Outstation Vehicles',
    subtitle: 'Vehicle types with OUTSTATION service type',
    color: '#2563eb',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'trip_types', label: 'Trip Type — OUTSTATION', why: 'Required for outstation bookings.', example: 'OUTSTATION · Service Code: OUTSTATION' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_types', label: 'Vehicle Type', why: 'Set serviceType to include OUTSTATION.', example: 'sedan · serviceType: LOCAL,OUTSTATION' },
      { groupKey: 'transport_masters', typeKey: 'vehicle_brands', label: 'Vehicle Brand', why: 'Fleet taxonomy for driver onboarding.', example: 'maruti · hyundai' },
    ],
  },
  {
    id: 'pricing',
    title: '2. Outstation Pricing',
    subtitle: 'One master: zone + vehicle + trip type',
    color: '#0d9488',
    steps: [
      { groupKey: '_external', typeKey: 'outstation_routes_tab', label: 'Outstation Pricing Management', why: 'Configure per zone, vehicle, One Way and Round Trip.', example: 'Sedan OW: base ₹500 + ₹14/km · RT: base ₹800 + ₹12/km · bata ₹300' },
      { groupKey: 'transport_masters', typeKey: 'outstation_pricing', label: 'Outstation Pricing Master', why: 'KM slabs, bata, toll/parking, GST, surge.', example: 'BLR_CENTRAL · sedan · one_way · min 50 km' },
    ],
  },
  {
    id: 'availability',
    title: '3. Availability',
    subtitle: 'When outstation bookings are accepted',
    color: '#059669',
    steps: [
      { groupKey: 'transport_masters', typeKey: 'service_availability_schedules', label: 'Availability Schedule', why: 'Day/time windows for outstation service.', example: 'Daily 05:00–22:00' },
    ],
  },
];

const FLOW_BY_KEY: Record<string, MasterSetupPhase[]> = {
  'delivery:food-delivery': FOOD_DELIVERY_SETUP,
  'delivery:grocery-delivery': GROCERY_DELIVERY_SETUP,
  'transport': TRANSPORT_SETUP,
  'transport:*': TRANSPORT_SETUP,
  'transport:transport': TRANSPORT_SETUP,
  'on_demand': ON_DEMAND_SETUP,
  'on_demand:*': ON_DEMAND_SETUP,
  'on_demand:home-services': ON_DEMAND_SETUP,
  video_consultation: VIDEO_CONSULTATION_SETUP,
  booking: BOOKING_SETUP,
};

export function getServiceSetupFlowMeta(hub: ExtendedServiceHubType | string, slug?: string): { title: string; subtitle: string } {
  if (hub === 'delivery' && slug === 'food-delivery') {
    return {
      title: 'Food & Grocery Delivery — Complete Flow',
      subtitle: '17 Food Add masters → 6-tab wizard. Swiggy-like order pipeline with OTP delivery.',
    };
  }
  if (hub === 'delivery' && slug === 'grocery-delivery') {
    return {
      title: 'Grocery Delivery — Complete Flow',
      subtitle: 'Zones + partner pricing + incentives → grocery masters → Grocery Add → slots & dispatch.',
    };
  }
  if (hub === 'transport' && slug === 'bike-rental') {
    return {
      title: 'Bike Rental Setup',
      subtitle: 'Fleet depots, rental-enabled vehicle types, and hour/km packages — like transport rental management.',
    };
  }
  if (hub === 'transport' && slug === 'car-rental') {
    return {
      title: 'Car Rental Setup',
      subtitle: 'Depots, vehicle taxonomy, and zone-wise rental packages with per-vehicle pricing.',
    };
  }
  if (hub === 'transport' && (slug === 'outstation' || slug === 'intercity')) {
    return {
      title: slug === 'intercity' ? 'Intercity Setup' : 'Outstation Setup',
      subtitle: 'City-to-city route packages with driver allowance, per-km rate, and round-trip multiplier.',
    };
  }
  if (hub === 'transport') {
    return {
      title: 'Transport Setup',
      subtitle: 'Configure ride modes, vehicle types, and pricing masters.',
    };
  }
  if (hub === 'on_demand') {
    return {
      title: 'Services Setup Guide',
      subtitle:
        'Categories (primary → sub via eye icon), skills, certificates, pricing templates (package blueprint), visit charges (doorstep fee), and cancel reasons — Seed from Catalog or Masters.',
    };
  }
  if (hub === 'video_consultation') {
    return { title: 'Video Consultation Setup Guide', subtitle: 'Consultation types, specializations, and session rules.' };
  }
  if (hub === 'booking') {
    return { title: 'Booking Setup Guide', subtitle: 'Venues, organizers, and cancellation policies.' };
  }
  return { title: 'Service Masters Guide', subtitle: 'Follow the recommended order to configure this sub-service.' };
}

function catalogHasType(catalog: MasterCatalogGroup[], groupKey: string, typeKey: string): boolean {
  if (groupKey === '_external') return true;
  const group = catalog.find((g) => g.key === groupKey);
  return !!group?.types.some((t) => t.key === typeKey);
}

/** Filter flow steps to types present in catalog; drop empty phases */
export function resolveServiceSetupPhases(
  hub: ExtendedServiceHubType | string,
  slug: string | undefined,
  catalog: MasterCatalogGroup[],
): MasterSetupPhase[] {
  const key = (hub === 'delivery' || hub === 'on_demand') && slug
    ? `${hub}:${slug}`
    : hub;
  const raw = FLOW_BY_KEY[key] || FLOW_BY_KEY[`${hub}:*`] || FLOW_BY_KEY[hub] || [];

  return raw
    .filter((phase) => phase.id !== 'coverage' || !['food-delivery', 'grocery-delivery', 'porter-service'].includes(slug || ''))
    .map((phase) => ({
      ...phase,
      steps: phase.steps.filter((step) => step.externalLink || catalogHasType(catalog, step.groupKey, step.typeKey)),
    }))
    .filter((phase) => phase.steps.length > 0);
}
